import { useState, useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { Table } from "react-bootstrap";
import { niceDateTime, niceNumber, classNames } from "@/lib/Util";
import { ModelData, calculateDurationMin, calculateNormativeBreakTime, Operation } from "@/lib/ModelDetails";
import DeviationBar from "@/components/dashboard/DeviationBar";
import OrderTableProductionModal from "@/components/manufacturing/OrderTableProductionModal";

const SAMPLE_SIZE = 10;
const BASE_RATIO = 1;

type OperationWithEstimates = Operation & {
    normBreakTime: number;
    estimationModelMin: number;
    estimationNormativeMin: number;
};

type Props = {
    model: ModelData;
    breakTimeMin: number;
};

/**
 * Table shows the latest operations history with estimates and errors.
 */
const LatestOperationsHistoryTable = ({ model, breakTimeMin = 0 }: Props) => {
    const { t } = useLingui();
    const [selectedOperationUuid, setSelectedOperationUuiId] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);

    const operations = model.latestOperations.length > 0 ? model.latestOperations.slice(0, SAMPLE_SIZE) : [];

    const operationsWithEstimates = useMemo(() => {
        return operations.map<OperationWithEstimates>(operation => {
            // Use specific break time if provided, otherwise calculate normative break time
            const normBreakTime =
                operation.breakTime !== undefined
                    ? operation.breakTime
                    : calculateNormativeBreakTime(operation.actualDurationH * 60, breakTimeMin);

            return {
                ...operation,
                normBreakTime,
                estimationModelMin: calculateDurationMin(
                    operation.quantity,
                    model.modelSetup,
                    model.modelSpeed,
                    normBreakTime
                ),
                estimationNormativeMin: calculateDurationMin(
                    operation.quantity,
                    model.normativeSetup,
                    model.normativeSpeed,
                    normBreakTime
                ),
            };
        });
    }, [operations, model, breakTimeMin]);

    const maxRatio = useMemo(() => {
        let max = BASE_RATIO;
        for (const op of operationsWithEstimates) {
            if (op.actualDateStartTs === null || op.actualDateEndTs === null || op.actualDurationH <= 0) {
                continue;
            }

            const actualDurationMin = op.actualDurationH * 60;
            max = Math.max(
                max,
                op.estimationModelMin / actualDurationMin,
                op.estimationNormativeMin / actualDurationMin
            );
        }

        return max;
    }, [operationsWithEstimates]);

    const selectedOperation = operations.find(operation => operation.operationUuid === selectedOperationUuid);

    return (
        <>
            {selectedOperation && selectedOperationUuid && (
                <OrderTableProductionModal
                    show={showModal}
                    selected_order_uuid={selectedOperationUuid ?? ""}
                    selected_order_external_id={selectedOperation.orderExternalId}
                    process_num={"0"}
                    onShowOrHideOrderModal={() => {
                        setShowModal(!showModal);
                    }}
                    onOrderSave={() => {}}
                    show_columns={[
                        "process_num",
                        "title",
                        "quantity_produced",
                        "quantity_scrap",
                        "quantity_total",
                        "output_stock",
                        "status",
                        "time_start",
                        "time_end",
                        "skip_sim",
                        "ignore_on_planning_board",
                    ]}
                />
            )}
            <div className="card p-0">
                <div className="card-title card-title-border-bottom p-3">{t`Latest Work-Orders History (max 10)`}</div>
                <div className="card-content">
                    {operations.length === 0 ? (
                        <div className="p-3 text-center text-muted">{t`No work orders found for this model.`}</div>
                    ) : (
                        <div className="table-responsive">
                            <Table size="sm" responsive className="dashboard-table">
                                <thead>
                                    <tr className="text-center">
                                        <th className="text-left">{t`Order ID`}</th>
                                        <th className="text-left">{t`Start`}</th>
                                        <th className="text-left">{t`End`}</th>
                                        <th className="text-right">{t`QTY`}</th>
                                        <th className="text-right">{t`Actual Dur. (min)`}</th>
                                        <th className="text-right">{t`Norm BreakTime`}</th>
                                        <th className="text-right">{t`Model Est. (min)`}</th>
                                        <th className="text-right">{t`Norm Est. (min)`}</th>
                                        <th className="text-left">{t`Error analysis`}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {operationsWithEstimates.map((operation, index) => {
                                        const hasStart = operation.actualDateStartTs !== null;
                                        const hasEnd = operation.actualDateEndTs !== null;
                                        const hasBothTimes = hasStart && hasEnd;
                                        const hasValidDurationForError = hasBothTimes && operation.actualDurationH > 0;

                                        const actualDurationMin = operation.actualDurationH * 60;
                                        const modelDeltaMin = hasValidDurationForError
                                            ? operation.estimationModelMin - actualDurationMin
                                            : 0;
                                        const normativeDeltaMin = hasValidDurationForError
                                            ? operation.estimationNormativeMin - actualDurationMin
                                            : 0;

                                        const dateStart = hasStart
                                            ? niceDateTime(new Date(operation.actualDateStartTs!))
                                            : "–";
                                        const dateEnd = hasEnd
                                            ? niceDateTime(new Date(operation.actualDateEndTs!))
                                            : "–";
                                        const duration = hasBothTimes ? niceNumber(actualDurationMin, 2, 2) : "–";

                                        const modelRatio = hasValidDurationForError
                                            ? operation.estimationModelMin / actualDurationMin
                                            : 0;
                                        const normativeRatio = hasValidDurationForError
                                            ? operation.estimationNormativeMin / actualDurationMin
                                            : 0;

                                        return (
                                            <tr key={index}>
                                                <td>
                                                    <a
                                                        href="#"
                                                        onClick={() => {
                                                            setSelectedOperationUuiId(operation.operationUuid);
                                                            setShowModal(true);
                                                        }}
                                                    >
                                                        {operation.orderExternalId}
                                                    </a>
                                                </td>
                                                <td>{dateStart}</td>
                                                <td>{dateEnd}</td>
                                                <td className="text-right font-weight-bold">
                                                    {niceNumber(operation.quantity, 2)}
                                                </td>
                                                <td className="text-right">{duration}</td>
                                                <td className="text-right column--norm-break-time">
                                                    {niceNumber(operation.normBreakTime, 2, 2)}
                                                </td>
                                                <td className="column--model-estimation">
                                                    <div className="d-flex justify-content-between align-items-center gap-2">
                                                        <span className="flex-grow-1 text-right">
                                                            {niceNumber(operation.estimationModelMin, 2, 2)}
                                                        </span>
                                                        <span
                                                            className={classNames("badge delta-badge flex-grow-1", {
                                                                "variant-success": modelDeltaMin > 0,
                                                                "variant-danger": modelDeltaMin < 0,
                                                                "variant-neutral": modelDeltaMin === 0,
                                                            })}
                                                        >
                                                            {modelDeltaMin > 0 ? "+" : ""}
                                                            {modelDeltaMin !== 0 ? niceNumber(modelDeltaMin, 2) : "–"}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="column--normative-estimation">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="flex-grow-1 text-right">
                                                            {niceNumber(operation.estimationNormativeMin, 2, 2)}
                                                        </span>
                                                        <span
                                                            className={classNames("badge delta-badge flex-grow-1", {
                                                                "variant-success": normativeDeltaMin > 0,
                                                                "variant-danger": normativeDeltaMin < 0,
                                                                "variant-neutral": normativeDeltaMin === 0,
                                                            })}
                                                        >
                                                            {normativeDeltaMin > 0 ? "+" : ""}
                                                            {normativeDeltaMin !== 0
                                                                ? niceNumber(normativeDeltaMin, 2)
                                                                : "–"}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    {hasValidDurationForError ? (
                                                        <div className="error-bars-container">
                                                            <span>{t`M`}:</span>
                                                            <DeviationBar
                                                                value={modelRatio}
                                                                baseValue={BASE_RATIO}
                                                                maxValue={maxRatio}
                                                            />
                                                            <span className="text-right">
                                                                {niceNumber(modelRatio * 100, 1, 1)}%
                                                            </span>
                                                            <span>{t`N`}:</span>
                                                            <DeviationBar
                                                                value={normativeRatio}
                                                                baseValue={BASE_RATIO}
                                                                maxValue={maxRatio}
                                                            />
                                                            <span className="text-right">
                                                                {niceNumber(normativeRatio * 100, 1, 1)}%
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        "–"
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default LatestOperationsHistoryTable;
